import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/axios';
import {
  History, Trash2, Edit, Clock,
  Search, ChevronLeft, ChevronRight, Filter, ArrowRight,
} from 'lucide-react';

const LIMIT = 20;

// ─── Libellés des champs ──────────────────────────────────────────────────────
const FIELD_LABELS = {
  montant:         'Montant',
  type:            'Type',
  methodePaiement: 'Paiement',
  categorie:       'Catégorie',
  description:     'Description',
  date:            'Date',
};

const TRANSACTION_FIELDS = Object.keys(FIELD_LABELS);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isValidDate = (val) => val != null && val !== '' && !isNaN(new Date(val).getTime());

const formatFieldValue = (key, val, currency) => {
  if (val === null || val === undefined || val === '') return '-';
  if (key === 'montant') return `${Number(val).toLocaleString()} ${currency}`;
  if (key === 'date')    return isValidDate(val) ? new Date(val).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  return String(val);
};

const hasChanged = (before, after, key) => {
  const b = before?.[key];
  const a = after?.[key];
  if (b === undefined && a === undefined) return false;
  if (key === 'date') {
    if (!isValidDate(b) && !isValidDate(a)) return false;
    if (!isValidDate(b) || !isValidDate(a)) return true;
    return new Date(b).toISOString() !== new Date(a).toISOString();
  }
  return String(b ?? '') !== String(a ?? '');
};

const getChangedFields = (before, after) =>
  TRANSACTION_FIELDS.filter((k) => hasChanged(before, after, k));

const formatLogDate = (dateStr, timezone) =>
  new Date(dateStr).toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(timezone && { timeZone: timezone }),
  });

// ─── Sous-composants ──────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  if (!type) return <span className="text-slate-400 text-xs">-</span>;
  return (
    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase ${
      ['Vente', 'Prestation'].includes(type) ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
    }`}>
      {type}
    </span>
  );
}

function FieldRow({ label, before, after, currency, fieldKey }) {
  const bVal = formatFieldValue(fieldKey, before, currency);
  const aVal = formatFieldValue(fieldKey, after, currency);
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider w-24 shrink-0 mt-0.5">
        {label}
      </span>
      <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
        {fieldKey === 'type' ? (
          <>
            <TypeBadge type={before} />
            <ArrowRight size={12} className="text-slate-300 shrink-0" />
            <TypeBadge type={after} />
          </>
        ) : (
          <>
            <span className="text-sm font-bold text-slate-400 line-through decoration-slate-300 break-all">
              {bVal}
            </span>
            <ArrowRight size={12} className="text-slate-300 shrink-0" />
            <span className="text-sm font-black text-slate-800 break-all">{aVal}</span>
          </>
        )}
      </div>
    </div>
  );
}

function DeletedTransactionCard({ data, currency }) {
  if (!data) return null;
  const rows = TRANSACTION_FIELDS.filter((k) => data[k] !== undefined && data[k] !== null && data[k] !== '');
  return (
    <div className="space-y-1">
      {rows.map((key) => (
        <div key={key} className="flex items-start gap-3 py-1.5 border-b border-slate-50 last:border-0">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider w-24 shrink-0 mt-0.5">
            {FIELD_LABELS[key]}
          </span>
          {key === 'type' ? (
            <TypeBadge type={data[key]} />
          ) : (
            <span className="text-sm font-bold text-slate-700 break-all">
              {formatFieldValue(key, data[key], currency)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

const AuditLogs = ({ salonDevise, salonTimezone }) => {
  const [logs, setLogs]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [pages, setPages]             = useState(1);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [action, setAction]           = useState('');
  const debounceRef                   = useRef(null);

  const currency = salonDevise === 'XOF' ? 'FCFA' : salonDevise === 'EUR' ? '€' : salonDevise === 'USD' ? '$' : (salonDevise || '');

  const fetchLogs = useCallback(async (p, s, a) => {
    setLoading(true);
    try {
      const res = await api.get('/admin/transactions/logs', {
        params: { page: p, limit: LIMIT, search: s, action: a },
      });
      setLogs(res.data.data   || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error('Erreur chargement logs', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Un seul useEffect avec toutes les dépendances correctes
  useEffect(() => {
    fetchLogs(page, searchDebounced, action);
  }, [page, searchDebounced, action, fetchLogs]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchDebounced(val), 400);
  };

  const handleActionChange = (val) => {
    setAction(val);
    setPage(1);
  };

  // ─── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 pb-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 flex items-center gap-3">
            <History className="text-blue-600" size={28} /> Journal
          </h1>
          <p className="text-slate-500 font-medium text-sm mt-1">
            {total} opération{total !== 1 ? 's' : ''} enregistrée{total !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Filtre type */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
            <select
              value={action}
              onChange={(e) => handleActionChange(e.target.value)}
              className="pl-9 pr-4 py-3 bg-white border border-slate-100 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-sm shadow-sm appearance-none cursor-pointer"
            >
              <option value="">Tous les types</option>
              <option value="UPDATE">Modifications</option>
              <option value="DELETE">Suppressions</option>
            </select>
          </div>
          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            <input
              type="text"
              value={search}
              placeholder="Rechercher..."
              className="w-full sm:w-64 pl-11 pr-4 py-3 bg-white border border-slate-100 rounded-2xl outline-none focus:ring-2 ring-blue-500/10 font-bold text-sm shadow-sm"
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="font-black text-slate-400 uppercase text-xs tracking-widest">Chargement...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-[2.5rem] p-16 text-center border-2 border-dashed border-slate-100">
          <div className="w-14 h-14 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <History size={28} />
          </div>
          <p className="text-slate-400 font-bold text-sm">Aucune opération enregistrée.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const isUpdate = log.action === 'UPDATE';
            const changedFields = isUpdate
              ? getChangedFields(log.details?.before, log.details?.after)
              : [];

            return (
              <div key={log._id} className="bg-white border border-slate-100 rounded-[2rem] p-5 md:p-6 shadow-sm">

                {/* En-tête de la carte */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                    isUpdate ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'
                  }`}>
                    {isUpdate ? <Edit size={18} /> : <Trash2 size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-black uppercase tracking-widest ${
                      isUpdate ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      {isUpdate ? 'Modification' : 'Suppression'}
                      {isUpdate && changedFields.length > 0 && (
                        <span className="ml-2 text-slate-400 normal-case tracking-normal font-bold">
                          - {changedFields.map(k => FIELD_LABELS[k]).join(', ')}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center text-slate-400 gap-1.5 text-xs font-medium mt-0.5">
                      <Clock size={11} />
                      {formatLogDate(log.date, salonTimezone)}
                    </div>
                  </div>
                  <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase shrink-0">
                    #{log.targetId?.slice(-6) || 'N/A'}
                  </span>
                </div>

                {/* Corps */}
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  {isUpdate ? (
                    changedFields.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Aucun changement détecté.</p>
                    ) : (
                      <div>
                        {changedFields.map((key) => (
                          <FieldRow
                            key={key}
                            fieldKey={key}
                            label={FIELD_LABELS[key]}
                            before={log.details.before?.[key]}
                            after={log.details.after?.[key]}
                            currency={currency}
                          />
                        ))}
                      </div>
                    )
                  ) : (
                    <>
                      <p className="text-[9px] font-black text-red-500 uppercase tracking-widest mb-3">
                        Transaction supprimée
                      </p>
                      <DeletedTransactionCard data={log.details?.deletedData} currency={currency} />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs font-bold text-slate-400">
            Page {page} / {pages} &nbsp;·&nbsp; {total} opération{total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft size={16} />
            </button>

            {Array.from({ length: pages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && arr[idx - 1] !== p - 1) acc.push('ellipsis');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === 'ellipsis' ? (
                  <span key={`e-${idx}`} className="px-1 text-slate-400 text-sm select-none">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setPage(item)}
                    className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${
                      page === item
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {item}
                  </button>
                )
              )}

            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
